/**
 * detailPollFields.ts — Phase 240 D-14/D-16/D-17: the runtime lockstep guard for BOTH MC polls,
 * plus the fleet card's tooltip composer.
 *
 * WHY THIS IS ITS OWN MODULE, and why it has ZERO imports: `src/js/mc.ts` pulls in
 * bootstrap/DataTables-adjacent modules, so a plain `node file.ts` test can never import it (the
 * same constraint 240-01 documented for `shared.ts`, which is why `utils/instants.ts` exists).
 * Putting the allow-list inside `mc.ts` and re-declaring it in the test would recreate the exact
 * two-mirrors-that-drift defect this whole phase exists to dismantle — the test would then pass
 * happily against a stale renderer. Keep this file dependency-free.
 *
 * The mechanism: each renderer declares, here, the payload keys it actually knows how to render. On
 * every poll tick it checks each player entry against that declaration BEFORE touching that
 * player's DOM. An unrecognized key means the renderer provably does not understand what the server
 * sent, so the markup is left exactly as the server rendered it — stale-but-complete beats
 * fresh-but-gutted (D-16's accepted trade). This has to fire by itself at runtime because this repo
 * has no CI: no `.github/workflows`, no `test` script in `package.json` or `composer.json`. A check
 * that only runs when someone remembers to run it is not a guard.
 *
 * 240-08 (D-17) extends the module from ONE mirror to BOTH: `renderDetailLivePlayerRows()` (the
 * detail page) and `diffPlayerHeads()` (the fleet card) each get a declared list, served by ONE
 * key-diff implementation. The fleet card's tooltip composer lives here too rather than in `mc.ts`,
 * for exactly the node-importability reason above — it is a pure string function with no DOM
 * dependency, so keeping it testable costs nothing and hiding it inside `mc.ts` would cost the
 * proof.
 */

/**
 * The keys the detail-poll renderer handles, mirroring `App\Utils\McPlayerPayload`'s own const of
 * the SAME NAME — that identical naming is deliberate, so the two lists can be cross-checked
 * mechanically (`tests/mc/reconcile.test.ts` reads the PHP file and deep-equals the two sorted
 * lists; it does NOT restate either one).
 *
 * ⚠ ADDING A FIELD REQUIRES UPDATING BOTH SIDES IN THE SAME COMMIT. That conscious-update
 * requirement IS the guard: PHP's pinned test goes RED if the const and the builder disagree, and
 * this set going stale makes the renderer refuse to rebuild rather than render a degraded row.
 *
 * NOTE the count: seventeen, not the fifteen 240-03 shipped with (itself not the twelve an older
 * draft of this design assumed). Phase 239 D-01/D-02 replaced the single `name` key with the
 * resolved identity quartet (`display_name`/`unresolved`/`is_online_mode`/`linkable`) and widened
 * the fleet poll to the same shape; Phase 241 D-28 then interleaved `dimensionLabel`/`biomeLabel`
 * directly after their raw `dimension`/`biome` twins — the server-resolved imported-mod-name label
 * riding alongside each raw key, never replacing it (D-03). The source of truth is the PHP const,
 * always — never this comment.
 */
export const DETAIL_POLL_FIELDS: ReadonlySet<string> = new Set([
    'display_name',
    'unresolved',
    'is_online_mode',
    'linkable',
    'uuid',
    'dimension',
    'dimensionLabel',
    'biome',
    'biomeLabel',
    'afk',
    'healthLabel',
    'isLowHealth',
    'food',
    'foodTitle',
    'ping',
    'pingTier',
    'effects',
]);

/**
 * The keys the FLEET-CARD renderer (`diffPlayerHeads()`) handles, mirroring
 * `App\Utils\McPlayerPayload::FLEET_CARD_FIELDS` — again the identical name, so
 * `tests/mc/reconcile.test.ts` can cross-check the two mechanically instead of restating either.
 *
 * ⚠ THIS IS A DECLARED SUBSET OF THE LIST ABOVE, NEVER AN INDEPENDENT LIST. That is D-17's whole
 * structural point, and the PHP side enforces it by deriving the fleet payload with
 * `array_intersect_key` against its own const rather than by hand-writing a second array. The alias
 * form below is the exact JS counterpart of the PHP declaration as it actually stands
 * (`public const FLEET_CARD_FIELDS = self::DETAIL_POLL_FIELDS;`): 240-03 found that Phase 239 had
 * already widened the fleet poll to full parity, because the fleet card's own avatar `alt`/`title`
 * and per-head profile link consume the resolved identity keys. Full parity is a subset in the ⊆
 * sense the pinned assertions on both sides check.
 *
 * WHEN THE PHP CONST IS EVER GENUINELY NARROWED, narrow this in the SAME commit: the cross-check
 * resolves the PHP alias, so a real narrowing on one side alone turns the test RED by design. Do
 * not "fix" that RED by restating names here — mirror whatever the PHP declares.
 */
export const FLEET_CARD_FIELDS: ReadonlySet<string> = DETAIL_POLL_FIELDS;

/**
 * The `window`-level detection channel (D-16). `console.error` ALONE is explicitly rejected: on
 * prod nobody has devtools open, so a console-only report tells no one — this repo's own
 * `graceful-degrade-silent-catch-masks-the-bug` scar. This marker survives a headless session, so
 * `tools/probe-235-poll-tick-render.mjs` can `page.evaluate(() => window.__MC_POLL_FIELD_GUARD__)`
 * and hard-fail on a non-empty array.
 *
 * Deliberately NOT a visible on-page element: D-16 rejected that (it would put a developer-facing
 * warning in front of a real viewer, and is one more state to design and verify at 320px).
 */
const GUARD_MARKER = '__MC_POLL_FIELD_GUARD__';

/** Bounded so a permanently-broken payload on a 30s tick can never grow this without limit. */
const GUARD_MARKER_MAX = 20;

/** Which mirror reported. A closed literal set — never payload data (T-240-33). */
export type PollRenderer = 'detail' | 'fleet';

/** One recorded detection. KEY NAMES ONLY — see `report()`. */
export interface FieldGuardReport {
    at: string;
    renderer: PollRenderer;
    unknown: string[];
}

/**
 * Returns the payload keys the given renderer does not handle.
 *
 * - `[]` when `player` is a non-null object whose keys are all in `allowed`.
 * - the full list of offending names (ALL of them, never just the first) otherwise.
 * - `['<not-an-object>']` when `player` is not a non-null object.
 *
 * ONE key-diff implementation serves BOTH declared lists (240-08 / D-17). A second copy of this
 * loop parameterized on a different set is precisely the two-mirrors defect this module exists to
 * dismantle, one level up. `allowed` defaults to the detail list so 240-07's call sites and its
 * existing assertions keep their exact one-argument shape.
 *
 * T-240-28: never throws. A guard that throws would abort the entire poll tick — strictly worse
 * than the desynchronization it protects against — so a non-object entry is reported as
 * un-understood rather than blowing up `Object.keys()`.
 *
 * Returning the names (rather than only a boolean) is what lets the test assert on WHICH keys were
 * reported without capturing `console.error`, which would couple the test to the reporting channel.
 */
export function unknownPlayerFields(player: unknown, allowed: ReadonlySet<string> = DETAIL_POLL_FIELDS): string[] {
    if (typeof player !== 'object' || player === null) {
        return ['<not-an-object>'];
    }
    return Object.keys(player as Record<string, unknown>).filter((key) => !allowed.has(key));
}

/**
 * Records a detection to BOTH channels. T-240-27: KEY NAMES ONLY — never a key's VALUE. An unknown
 * key's *name* is a developer-facing schema fact; its *value* is player data and must not be
 * copied into a log line or a client-readable marker. Do not "helpfully" widen this to include the
 * offending values while debugging.
 */
function report(renderer: PollRenderer, unknown: string[]): void {
    const message = `[mc] ${renderer} poll: unrecognized player payload key(s) ${unknown.join(', ')} — `
        + 'renderer left the server-rendered markup untouched (Phase 240 D-14/D-16/D-17). Update the '
        + 'declared list in BOTH src/js/utils/detailPollFields.ts and app/Utils/McPlayerPayload.php.';
    console.error(message);

    // Channel 2 — survives a headless prod session with no devtools attached.
    try {
        const scope = globalThis as unknown as Record<string, FieldGuardReport[] | undefined>;
        const log = scope[GUARD_MARKER] ?? [];
        if (log.length < GUARD_MARKER_MAX) {
            log.push({ at: new Date().toISOString(), renderer, unknown: unknown.slice() });
        }
        scope[GUARD_MARKER] = log;
    } catch {
        // A frozen/absent global scope must never break the poll tick — the console line above
        // already carries the same information for anyone at a keyboard.
    }
}

/**
 * `true` when every key of `player` is declared above; otherwise reports ALL offending keys once
 * and returns `false`.
 *
 * THE ASYMMETRY IS DELIBERATE. A *missing* known key returns `true`: the renderer's own per-field
 * null guards (`player.food != null`, `hasHealth`, …) already handle a shrinking payload honestly,
 * so a narrower payload is a legitimate wire state, not a desynchronization. An *unknown* key
 * returns `false`: the server is sending something this renderer has never been taught to render,
 * which is exactly the D-14 condition — the renderer's mirror of the payload contract is stale.
 */
export function reconcilePlayerFields(player: Record<string, unknown>): boolean {
    const unknown = unknownPlayerFields(player);
    if (unknown.length === 0) {
        return true;
    }
    report('detail', unknown);
    return false;
}

/**
 * The fleet card's half of the same guard (D-17 folds `diffPlayerHeads()` into D-14/D-16's promise,
 * so SC6's "the two mirrors" means EVERY mirror). Same asymmetry, same reporting channel, same
 * single key-diff above — only the declared list differs.
 *
 * `false` means `diffPlayerHeads()` skips that player entirely: an already-present avatar keeps the
 * markup the server rendered for it, and no new avatar is invented for a payload the renderer
 * cannot read.
 */
export function reconcileFleetPlayerFields(player: Record<string, unknown>): boolean {
    const unknown = unknownPlayerFields(player, FLEET_CARD_FIELDS);
    if (unknown.length === 0) {
        return true;
    }
    report('fleet', unknown);
    return false;
}

/**
 * The minimum shape `buildFleetTooltip()` reads. Declared structurally HERE rather than imported
 * from `mc.ts`'s `PlayerData`, because an import — even a type-only one — would end this module's
 * zero-import property and with it the node test's ability to load it. `PlayerData` satisfies this
 * interface structurally, so `mc.ts` passes its own objects straight in with no cast and no second
 * type to keep in sync.
 *
 * Note which keys are ABSENT and stay absent: the hunger sub-metric tooltip and its saturation
 * input. 235 D-16 keeps those detail-page-only, `default.latte` does not render them on a head, and
 * D-17's entire point is that the poll path REPRODUCES the server-rendered path rather than
 * inventing a richer one. Widening this interface to reach them fails 240-08's acceptance criteria
 * on purpose.
 */
export interface FleetTooltipPlayer {
    display_name: string;
    healthLabel?: string | null;
    food?: number | null;
    ping?: number | null;
    effects?: readonly unknown[] | null;
}

/**
 * Composes the fleet-card avatar tooltip, reproducing `default.latte`'s
 * `implode(' — ', array_filter([...]))` exactly — same join order, same segment copy, same omission
 * of every absent segment TOGETHER WITH its separator. The separator is a space, U+2014 EM DASH,
 * space.
 *
 * Segment order (locked by 235-UI-SPEC §7.4 and the template):
 *   1. resolved display name — always present
 *   2. `{healthLabel} HP`
 *   3. `{food}/20 food`
 *   4. `{ping}ms`
 *   5. the effect labels joined with `, `
 *
 * 235 D-11 — A NULL PING RENDERS NOTHING. Never `0ms`, never a dash, never a placeholder. The wire
 * deliberately never sends a lying `0` (the PHP builder coalesces nothing), and neither does this.
 * A genuine `0` from the wire is a real measurement and DOES render, which is why the guards below
 * are `!= null` rather than truthiness checks — `food: 0` (a starving player) and `ping: 0` are
 * values, not absences.
 *
 * T-240-31 — every effect element is guarded before its label is read. A malformed entry is
 * skipped, not thrown on: a throwing tooltip builder would abort the whole `diffPlayerHeads()` pass
 * and freeze the fleet card, strictly worse than the bug being fixed. The label key is the one
 * `McPlayerPayload::buildEffects()` emits.
 */
export function buildFleetTooltip(player: FleetTooltipPlayer): string {
    const effectLabels: string[] = [];
    if (Array.isArray(player.effects)) {
        for (const effect of player.effects) {
            if (typeof effect !== 'object' || effect === null) {
                continue;
            }
            const label = (effect as Record<string, unknown>).label;
            if (typeof label === 'string' && label !== '') {
                effectLabels.push(label);
            }
        }
    }

    const segments: (string | null)[] = [
        player.display_name,
        player.healthLabel != null ? `${player.healthLabel} HP` : null,
        player.food != null ? `${player.food}/20 food` : null,
        player.ping != null ? `${player.ping}ms` : null,
        effectLabels.length > 0 ? effectLabels.join(', ') : null,
    ];

    // Mirrors `array_filter(…, fn ($part) => $part !== null)`, widened to also drop a non-string or
    // empty segment: a payload arriving without its identity key must degrade to an empty tooltip,
    // never to the string "undefined" or to a stray leading separator.
    return segments.filter((part): part is string => typeof part === 'string' && part !== '').join(' — ');
}
